import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export interface CpmButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?:
        | 'navy'
        | 'outline'
        | 'outlinePurple'
        | 'ghost'
        | 'danger'
        | 'warn'
        | 'quietDanger'
        | 'success'
        | 'selection'
        | 'selectionGhost';
    /** `modal` = design card-modal CTA height (40px). */
    size?: 'sm' | 'md' | 'modal' | 'lg' | 'selection';
    isLoading?: boolean;
    leadingIcon?: ReactNode;
}

const VARIANT: Record<NonNullable<CpmButtonProps['variant']>, string> = {
    navy: 'bg-[var(--cpm-brand-navy)] text-white shadow-[var(--cpm-shadow-cta)] hover:brightness-110 hover:-translate-y-px',
    outline:
        'bg-white text-[var(--cpm-text-secondary)] border-[1.5px] border-[var(--cpm-border-strong)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)] hover:bg-[var(--cpm-surface-subtle)]',
    outlinePurple:
        'bg-white text-[var(--cpm-brand-purple)] border-[1.5px] border-[var(--cpm-brand-purple)] hover:bg-[var(--cpm-purple-tint-13)]',
    ghost: 'bg-transparent text-[var(--cpm-text-secondary)] border-[1.5px] border-transparent hover:bg-[var(--cpm-surface-subtle)] hover:text-[var(--cpm-brand-navy)]',
    danger: 'bg-[var(--cpm-error)] text-white shadow-[var(--cpm-shadow-danger-cta)] hover:brightness-110 hover:-translate-y-px',
    warn: 'bg-[var(--cpm-warning-bg)] text-[var(--cpm-warning-text)] border-[1.5px] border-[var(--cpm-warning-border)] hover:brightness-105 hover:-translate-y-px',
    quietDanger:
        'bg-transparent text-[var(--cpm-shortfall-text-med)] border-[1.5px] border-dashed border-[var(--cpm-border-strong)] hover:bg-[var(--cpm-shortfall-bg)] hover:border-[var(--cpm-shortfall-border)] hover:text-[var(--cpm-shortfall-text)]',
    success:
        'bg-[var(--cpm-success-text-light)] text-white shadow-[var(--cpm-shadow-success-cta)] hover:brightness-110 hover:-translate-y-px',
    selection: 'bg-white text-[var(--cpm-brand-navy)] hover:brightness-105 hover:-translate-y-px',
    selectionGhost: 'bg-white/12 text-white border border-white/28 hover:bg-white/20',
};

/** Absolute px sizes — avoid rem (app :root is 14px). */
const SIZE: Record<NonNullable<CpmButtonProps['size']>, string> = {
    sm: 'h-9 px-[15px] text-[12.5px]',
    md: 'h-[38px] px-4 text-[13.5px]',
    modal: 'h-10 px-[18px] text-[13.5px]',
    lg: 'h-11 px-[18px] text-[13.5px]',
    selection: 'h-10 px-[18px] text-[13.5px]',
};

const SIZE_STYLE: Record<
    NonNullable<CpmButtonProps['size']>,
    { height: number; paddingInline: number; fontSize: number }
> = {
    sm: { height: 36, paddingInline: 15, fontSize: 12.5 },
    md: { height: 38, paddingInline: 16, fontSize: 13.5 },
    modal: { height: 40, paddingInline: 18, fontSize: 13.5 },
    lg: { height: 44, paddingInline: 18, fontSize: 13.5 },
    selection: { height: 40, paddingInline: 18, fontSize: 13.5 },
};

export const CpmButton = forwardRef<HTMLButtonElement, CpmButtonProps>(
    (
        {
            variant = 'navy',
            size = 'md',
            isLoading = false,
            leadingIcon,
            className = '',
            children,
            disabled,
            style,
            ...props
        },
        ref
    ) => {
        const sizePx = SIZE_STYLE[size];
        return (
            <button
                ref={ref}
                type="button"
                disabled={disabled || isLoading}
                className={[
                    'cpm-btn inline-flex items-center justify-center gap-2 rounded-[var(--cpm-radius-10)] tracking-[-0.01em] whitespace-nowrap transition-[filter,transform,background,border-color,color] duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-50',
                    VARIANT[variant],
                    SIZE[size],
                    className,
                ].join(' ')}
                style={{
                    fontFamily: 'var(--cpm-font-sans)',
                    fontWeight: 800,
                    fontSize: sizePx.fontSize,
                    height: sizePx.height,
                    paddingLeft: sizePx.paddingInline,
                    paddingRight: sizePx.paddingInline,
                    ...style,
                }}
                {...props}
            >
                {isLoading ? (
                    'Working…'
                ) : (
                    <>
                        {leadingIcon}
                        {children}
                    </>
                )}
            </button>
        );
    }
);

CpmButton.displayName = 'CpmButton';
export default CpmButton;
